import styled from '@emotion/styled';
import { useMemo } from 'react';
import { mergeProps, useFocusRing } from 'react-aria';

import { ListBox, useOptionContext } from '../list-box/ListBox';
import { ListItem } from '../list-box/ListItem';

import { useI18n } from '../../hooks/useI18nFormatters';
import { useZone, useZonesTree } from '../../store/zones/useZones.ts';

import { scrollbars } from '../../theme/elements/scrollbars';
import { su } from '../../theme/functions/su';
import { HomeyImages } from '../../theme/HomeyImages';
import { theme } from '../../theme/theme';

export function ZoneListBox(props) {
  const { i18n } = useI18n();

  const zones = useZonesTree();

  const items = useZoneItems({ zones });

  function renderItem() {
    return <ZoneItem />;
  }

  return (
    <ZoneListBox.ListBox
      aria-label={i18n.messageFormatter('zone.title')}
      items={items}
      selectedKeys={props.selectedKeys}
      renderItem={renderItem}
      onSelectionChange={props.onSelectionChange}
    />
  );
}

ZoneListBox.ListBox = styled(ListBox)`
  ${scrollbars.dark};
  flex: 1 1 auto;
  width: 300px;
  padding: ${su(1, 1)};
  overflow: auto;
`;

function useZoneItems({ zones }) {
  return useMemo(() => {
    return (
      zones.tree?.list?.map((zone) => {
        const zoneData = zones.byId[zone.id];

        return {
          key: zone.id,
          type: 'zone',
          textValue: zoneData.name,
          context: {
            zone,
          },
        };
      }) ?? []
    );
  }, [zones.tree, zones.byId]);
}

export function ZoneItem(props) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars -- intentionally unused
  const { ref, option, state, item, value } = useOptionContext();

  const focusRing = useFocusRing();

  const level = value.context.zone.level ?? 0;
  const { zone = {} } = useZone({ zoneId: item.key });

  return (
    <ListItem.Root
      {...mergeProps(option.optionProps, focusRing.focusProps)}
      ref={ref}
      className={props.className}
      style={{ '--indent-level': level }}
      as={props.as}
      data-is-selected={option.isSelected}
      data-is-focused={option.isFocused}
      data-is-pressed={option.isPressed}
      data-is-disabled={option.isDisabled}
      data-is-focus-visible={focusRing.isFocusVisible}
    >
      <ListItem.Inner>
        <ListItem.Icon
          source={HomeyImages.getIconForZone(zone.icon)}
          size={theme.icon.size_default}
          color={`var(--module-glyph-color`}
        />

        <ListItem.Name>{zone.name}</ListItem.Name>
      </ListItem.Inner>
    </ListItem.Root>
  );
}
